import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtHM } from '../../lib/formatHelpers';

/**
 * V2 PilotageCreatorsTable — Liste paginée + sortable des créateurs.
 *
 * NoiRock adaptation : on prend les colonnes cleartext de creator_current_month
 * (display_name, tiktok_username, server, tier_current, days_since_joining,
 * status) + agrégation Excel depuis creator_metrics_daily (sessions, hours, days)
 * pour la période sélectionnée.
 *
 * Pas de chiffrage AES — uniquement cleartext (R2 préservé : Super Admin ne voit
 * jamais ces données, c'est admin/tenant_admin/agency_owner qui y accèdent).
 *
 * Props :
 *  - data       : [{ creator_id, display_name, tiktok_username, server, tier_current, sessions, hours, days, status }]
 *  - loading    : bool
 *  - period     : string (pour libellé)
 */
const PAGE_SIZE = 25;

const SortHeader = ({ field, label, sort, onSort, align = 'left' }) => {
  const active = sort.field === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold transition-colors ${
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
      } ${align === 'right' ? 'ml-auto' : ''}`}
    >
      {label}
      {active && (
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path d={sort.dir === 'asc' ? 'M4 2l3 4H1z' : 'M4 6L1 2h6z'} fill="currentColor" />
        </svg>
      )}
    </button>
  );
};

const PilotageCreatorsTable = ({ data = [], loading = false, period = 'month' }) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ field: 'sessions', dir: 'desc' });
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      const av = a[sort.field] ?? 0;
      const bv = b[sort.field] ?? 0;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [data, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageData = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = (field) => {
    setSort((s) => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }));
    setPage(0);
  };

  if (loading) {
    return <div className="rounded-2xl bg-card border border-border h-64 skeleton" />;
  }

  return (
    <section className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-surface-2 flex items-center justify-between">
        <div className="font-heading font-semibold text-foreground text-sm">
          {t('adminV2.table.title', 'Creators')}
          <span className="ml-2 text-xs text-muted-foreground font-normal">({fmtNum(sorted.length)})</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">
          {t(`adminV2.periods.${period}`, period)}
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-2">
              <th className="text-left px-4 py-2.5">
                <SortHeader field="display_name" label={t('adminV2.table.colCreator', 'Creator')} sort={sort} onSort={handleSort} />
              </th>
              <th className="text-left px-3 py-2.5">
                <SortHeader field="server" label={t('adminV2.table.colServer', 'Server')} sort={sort} onSort={handleSort} />
              </th>
              <th className="text-left px-3 py-2.5">
                <SortHeader field="tier_current" label={t('adminV2.table.colTier', 'Tier')} sort={sort} onSort={handleSort} />
              </th>
              <th className="text-right px-3 py-2.5">
                <SortHeader field="sessions" label={t('adminV2.table.colSessions', 'Sessions')} sort={sort} onSort={handleSort} align="right" />
              </th>
              <th className="text-right px-3 py-2.5">
                <SortHeader field="hours" label={t('adminV2.table.colHours', 'Hours')} sort={sort} onSort={handleSort} align="right" />
              </th>
              <th className="text-right px-3 py-2.5">
                <SortHeader field="days" label={t('adminV2.table.colDays', 'Days')} sort={sort} onSort={handleSort} align="right" />
              </th>
              <th className="text-right px-4 py-2.5">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                  {t('adminV2.table.colStatus', 'Status')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  {t('adminV2.table.empty', 'No creators in this period.')}
                </td>
              </tr>
            )}
            {pageData.map((c) => (
              <tr key={c.creator_id} className="border-b border-border last:border-b-0 hover:bg-muted/40">
                <td className="px-4 py-2.5">
                  <Link to={`/admin/creators/${c.creator_id}`} className="text-foreground hover:text-primary transition-colors">
                    <div className="font-medium truncate max-w-[200px]">{c.display_name || '—'}</div>
                    {c.tiktok_username && (
                      <div className="text-xs text-muted-foreground font-data truncate max-w-[200px]">@{c.tiktok_username}</div>
                    )}
                  </Link>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.server || '—'}</td>
                <td className="px-3 py-2.5 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-muted text-foreground font-data">
                    T{c.tier_current ?? '—'}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(c.sessions)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtHM((c.hours || 0) * 3600)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(c.days)}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    c.status === 'active'
                      ? 'bg-success/10 text-success'
                      : c.status === 'left'
                      ? 'bg-error/10 text-error'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {c.status || '—'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards (R3 mobile-first) */}
      <div className="md:hidden divide-y divide-border">
        {pageData.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            {t('adminV2.table.empty', 'No creators in this period.')}
          </div>
        )}
        {pageData.map((c) => (
          <Link
            key={c.creator_id}
            to={`/admin/creators/${c.creator_id}`}
            className="block px-4 py-3 hover:bg-muted/40 transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-foreground truncate">{c.display_name || '—'}</div>
                {c.tiktok_username && (
                  <div className="text-xs text-muted-foreground font-data truncate">@{c.tiktok_username}</div>
                )}
              </div>
              <span className="px-2 py-0.5 rounded-full bg-muted text-foreground text-xs font-data shrink-0">
                T{c.tier_current ?? '—'}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70">{t('adminV2.table.colSessions', 'Sessions')}</div>
                <div className="font-medium tabular-nums">{fmtNum(c.sessions)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70">{t('adminV2.table.colHours', 'Hours')}</div>
                <div className="font-medium tabular-nums">{fmtHM((c.hours || 0) * 3600)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70">{t('adminV2.table.colDays', 'Days')}</div>
                <div className="font-medium tabular-nums">{fmtNum(c.days)}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-border flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {t('adminV2.table.page', 'Page')} {page + 1} / {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 rounded border border-border hover:bg-muted disabled:opacity-40"
            >
              {t('common.prev', 'Prev')}
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded border border-border hover:bg-muted disabled:opacity-40"
            >
              {t('common.next', 'Next')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default PilotageCreatorsTable;
